export type Bounds3 = { min: [number, number, number]; max: [number, number, number]; size: [number, number, number] };
export type StlInspection = {
  encoding: "binary" | "ascii";
  triangles: number;
  bounds: Bounds3;
  surfaceArea: number;
  signedVolumeEstimate: number;
  previewVertices: Float32Array;
  previewTriangles: number;
};
export type StlTransform = {
  scale: [number, number, number];
  rotateDegrees: [number, number, number];
  placeMinimumAtOrigin: boolean;
};
export type MeshHealthReport = {
  triangles: number;
  uniqueVertices: number;
  degenerateTriangles: number;
  duplicateTriangles: number;
  boundaryEdges: number;
  nonManifoldEdges: number;
  watertightCandidate: boolean;
};
export type ConservativeRepairResult = {
  buffer: ArrayBuffer;
  before: MeshHealthReport;
  after: MeshHealthReport;
  removedDegenerateTriangles: number;
  removedDuplicateTriangles: number;
};
export type AxisOrientationCandidate = { up: "+X" | "-X" | "+Y" | "-Y" | "+Z" | "-Z"; footprint: [number, number]; height: number; footprintArea: number; estimatedOverhangArea: number; estimatedOverhangPercent: number };
export type AxisOrientationAnalysis = { triangles: number; thresholdDegreesFromVertical: number; surfaceArea: number; candidates: AxisOrientationCandidate[] };
export type StlComparisonSide = { triangles: number; bounds: Bounds3; surfaceArea: number; signedVolumeEstimate: number };
export type StlGeometryComparison = {
  left: StlComparisonSide;
  right: StlComparisonSide;
  matchedTriangles: number;
  onlyLeftTriangles: number;
  onlyRightTriangles: number;
  exactTriangleMultisetMatch: boolean;
  triangleJaccardPercent: number;
  dimensionDelta: [number, number, number];
  surfaceAreaDelta: number;
  signedVolumeDelta: number;
};
export type ObjInspection = {
  vertices: number;
  faces: number;
  triangles: number;
  groups: number;
  objects: number;
  materialLibraries: number;
  bounds: Bounds3;
  surfaceArea: number;
  previewVertices: Float32Array;
  previewTriangles: number;
  ignoredStatements: string[];
};
export type MeshConversionResult = {
  buffer: ArrayBuffer;
  sourceTriangles: number;
  outputTriangles: number;
  outputBytes: number;
  outputFormat: "OBJ" | "ASCII OFF" | "binary STL" | "binary little-endian PLY" | "GLB 2.0" | "AMF 1.2";
  warnings?: string[];
};

const MAX_TRIANGLES = 1_000_000;

function inspectTriangles(triangles: number[], encoding: "binary" | "ascii", triangleCount: number, previewLimit: number): StlInspection {
  if (triangleCount <= 0 || triangleCount > MAX_TRIANGLES) throw new Error(`STL triangle count must be between 1 and ${MAX_TRIANGLES.toLocaleString()}.`);
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  let area = 0;
  let signedVolume = 0;
  const stride = Math.max(1, Math.ceil(triangleCount / previewLimit));
  const preview: number[] = [];

  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const offset = triangle * 9;
    const ax = triangles[offset], ay = triangles[offset + 1], az = triangles[offset + 2];
    const bx = triangles[offset + 3], by = triangles[offset + 4], bz = triangles[offset + 5];
    const cx = triangles[offset + 6], cy = triangles[offset + 7], cz = triangles[offset + 8];
    const values = [ax, ay, az, bx, by, bz, cx, cy, cz];
    if (values.some((value) => !Number.isFinite(value))) throw new Error("STL contains a non-finite vertex coordinate.");
    for (let vertex = 0; vertex < 3; vertex += 1) {
      for (let axis = 0; axis < 3; axis += 1) {
        const value = values[vertex * 3 + axis];
        min[axis] = Math.min(min[axis], value);
        max[axis] = Math.max(max[axis], value);
      }
    }
    const abx = bx - ax, aby = by - ay, abz = bz - az;
    const acx = cx - ax, acy = cy - ay, acz = cz - az;
    const crossX = aby * acz - abz * acy;
    const crossY = abz * acx - abx * acz;
    const crossZ = abx * acy - aby * acx;
    area += Math.hypot(crossX, crossY, crossZ) / 2;
    signedVolume += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6;
    if (triangle % stride === 0) preview.push(...values);
  }

  return {
    encoding,
    triangles: triangleCount,
    bounds: { min, max, size: [max[0] - min[0], max[1] - min[1], max[2] - min[2]] },
    surfaceArea: area,
    signedVolumeEstimate: Math.abs(signedVolume),
    previewVertices: new Float32Array(preview),
    previewTriangles: preview.length / 9
  };
}

function parseBinary(buffer: ArrayBuffer, previewLimit: number): StlInspection | undefined {
  if (buffer.byteLength < 84) return undefined;
  const view = new DataView(buffer);
  const count = view.getUint32(80, true);
  const expected = 84 + count * 50;
  if (count === 0 || expected > buffer.byteLength) return undefined;
  if (count > MAX_TRIANGLES) throw new Error(`STL exceeds the provisional ${MAX_TRIANGLES.toLocaleString()}-triangle limit.`);
  const values = new Array<number>(count * 9);
  let output = 0;
  for (let triangle = 0; triangle < count; triangle += 1) {
    const base = 84 + triangle * 50 + 12;
    for (let coordinate = 0; coordinate < 9; coordinate += 1) values[output++] = view.getFloat32(base + coordinate * 4, true);
  }
  return inspectTriangles(values, "binary", count, previewLimit);
}

function parseAscii(buffer: ArrayBuffer, previewLimit: number): StlInspection {
  const text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
  const vertexPattern = /\bvertex\s+([-+\d.eE]+)\s+([-+\d.eE]+)\s+([-+\d.eE]+)/g;
  const values: number[] = [];
  let match: RegExpExecArray | null;
  while ((match = vertexPattern.exec(text)) !== null) {
    values.push(Number(match[1]), Number(match[2]), Number(match[3]));
    if (values.length / 9 > MAX_TRIANGLES) throw new Error(`STL exceeds the provisional ${MAX_TRIANGLES.toLocaleString()}-triangle limit.`);
  }
  if (values.length === 0 || values.length % 9 !== 0) throw new Error("ASCII STL does not contain complete triangle vertex groups.");
  return inspectTriangles(values, "ascii", values.length / 9, previewLimit);
}

export function inspectStl(buffer: ArrayBuffer, previewLimit = 8_000): StlInspection {
  return parseBinary(buffer, previewLimit) ?? parseAscii(buffer, previewLimit);
}

export function readStlTriangleValues(buffer: ArrayBuffer): { values: number[]; count: number } {
  if (buffer.byteLength >= 84) {
    const view = new DataView(buffer);
    const count = view.getUint32(80, true);
    if (count > 0 && 84 + count * 50 <= buffer.byteLength) {
      if (count > MAX_TRIANGLES) throw new Error(`STL exceeds the provisional ${MAX_TRIANGLES.toLocaleString()}-triangle limit.`);
      const values = new Array<number>(count * 9);
      let output = 0;
      for (let triangle = 0; triangle < count; triangle += 1) {
        const base = 84 + triangle * 50 + 12;
        for (let coordinate = 0; coordinate < 9; coordinate += 1) values[output++] = view.getFloat32(base + coordinate * 4, true);
      }
      return { values, count };
    }
  }
  const text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
  const pattern = /\bvertex\s+([-+\d.eE]+)\s+([-+\d.eE]+)\s+([-+\d.eE]+)/g;
  const values: number[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    values.push(Number(match[1]), Number(match[2]), Number(match[3]));
    if (values.length / 9 > MAX_TRIANGLES) throw new Error(`STL exceeds the provisional ${MAX_TRIANGLES.toLocaleString()}-triangle limit.`);
  }
  if (values.length === 0 || values.length % 9 !== 0) throw new Error("ASCII STL does not contain complete triangle vertex groups.");
  return { values, count: values.length / 9 };
}

export function analyzeAxisOrientations(buffer: ArrayBuffer, thresholdDegreesFromVertical = 45): AxisOrientationAnalysis {
  if (!Number.isFinite(thresholdDegreesFromVertical) || thresholdDegreesFromVertical < 0 || thresholdDegreesFromVertical >= 90) throw new Error("Overhang threshold must be at least 0° and less than 90° from vertical.");
  const { values, count } = readStlTriangleValues(buffer), min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity], directions = [
    { up: "+X" as const, axis: 0, sign: 1 }, { up: "-X" as const, axis: 0, sign: -1 }, { up: "+Y" as const, axis: 1, sign: 1 }, { up: "-Y" as const, axis: 1, sign: -1 }, { up: "+Z" as const, axis: 2, sign: 1 }, { up: "-Z" as const, axis: 2, sign: -1 }
  ], overhang = new Array<number>(directions.length).fill(0), threshold = Math.sin(thresholdDegreesFromVertical * Math.PI / 180); let surfaceArea = 0;
  for (let offset = 0; offset < values.length; offset += 9) {
    for (let vertex = 0; vertex < 3; vertex += 1) for (let axis = 0; axis < 3; axis += 1) { const value = values[offset + vertex * 3 + axis]; if (!Number.isFinite(value)) throw new Error("STL contains a non-finite vertex coordinate."); min[axis] = Math.min(min[axis], value); max[axis] = Math.max(max[axis], value); }
    const ax = values[offset], ay = values[offset + 1], az = values[offset + 2], abx = values[offset + 3] - ax, aby = values[offset + 4] - ay, abz = values[offset + 5] - az, acx = values[offset + 6] - ax, acy = values[offset + 7] - ay, acz = values[offset + 8] - az;
    const normal = [aby * acz - abz * acy, abz * acx - abx * acz, abx * acy - aby * acx], length = Math.hypot(...normal); if (length === 0) continue; const area = length / 2; surfaceArea += area;
    directions.forEach((direction, index) => { const normalTowardUp = normal[direction.axis] * direction.sign / length; if (normalTowardUp < -threshold) overhang[index] += area; });
  }
  const size = max.map((value, axis) => value - min[axis]), candidates = directions.map((direction, index): AxisOrientationCandidate => { const footprintAxes = [0,1,2].filter((axis) => axis !== direction.axis), footprint: [number, number] = [size[footprintAxes[0]], size[footprintAxes[1]]]; return { up: direction.up, footprint, height: size[direction.axis], footprintArea: footprint[0] * footprint[1], estimatedOverhangArea: overhang[index], estimatedOverhangPercent: surfaceArea ? overhang[index] / surfaceArea * 100 : 0 }; });
  candidates.sort((a, b) => a.estimatedOverhangArea - b.estimatedOverhangArea || a.height - b.height || a.footprintArea - b.footprintArea || a.up.localeCompare(b.up));
  return { triangles: count, thresholdDegreesFromVertical, surfaceArea, candidates };
}

function comparisonVertexKey(values: number[], offset: number): string {
  const clean = (value: number) => Object.is(value, -0) ? 0 : value;
  return `${clean(values[offset])},${clean(values[offset + 1])},${clean(values[offset + 2])}`;
}

function comparisonTriangleKey(values: number[], offset: number): string {
  return [comparisonVertexKey(values, offset), comparisonVertexKey(values, offset + 3), comparisonVertexKey(values, offset + 6)].sort().join("|");
}

function summarizeComparisonSide(values: number[], triangles: number): StlComparisonSide {
  const min: [number, number, number] = [Infinity, Infinity, Infinity], max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  let surfaceArea = 0, signedVolumeEstimate = 0;
  for (let offset = 0; offset < values.length; offset += 9) {
    for (let coordinate = 0; coordinate < 9; coordinate += 1) {
      const value = values[offset + coordinate];
      if (!Number.isFinite(value)) throw new Error("STL contains a non-finite vertex coordinate.");
      const axis = coordinate % 3; min[axis] = Math.min(min[axis], value); max[axis] = Math.max(max[axis], value);
    }
    const ax = values[offset], ay = values[offset + 1], az = values[offset + 2], bx = values[offset + 3], by = values[offset + 4], bz = values[offset + 5], cx = values[offset + 6], cy = values[offset + 7], cz = values[offset + 8];
    const abx = bx - ax, aby = by - ay, abz = bz - az, acx = cx - ax, acy = cy - ay, acz = cz - az;
    surfaceArea += Math.hypot(aby * acz - abz * acy, abz * acx - abx * acz, abx * acy - aby * acx) / 2;
    signedVolumeEstimate += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6;
  }
  return { triangles, bounds: { min, max, size: [max[0] - min[0], max[1] - min[1], max[2] - min[2]] }, surfaceArea, signedVolumeEstimate: Math.abs(signedVolumeEstimate) };
}

export function compareStlGeometry(leftBuffer: ArrayBuffer, rightBuffer: ArrayBuffer): StlGeometryComparison {
  const byteLimit = 10 * 1024 * 1024, triangleLimit = 200_000;
  if (leftBuffer.byteLength > byteLimit || rightBuffer.byteLength > byteLimit) throw new Error("Each STL must be 10 MiB or smaller for exact-coordinate comparison.");
  const leftGeometry = readStlTriangleValues(leftBuffer), rightGeometry = readStlTriangleValues(rightBuffer);
  if (leftGeometry.count > triangleLimit || rightGeometry.count > triangleLimit) throw new Error(`Each STL must contain at most ${triangleLimit.toLocaleString()} triangles for exact-coordinate comparison.`);
  const remaining = new Map<string, number>();
  for (let offset = 0; offset < leftGeometry.values.length; offset += 9) {
    const key = comparisonTriangleKey(leftGeometry.values, offset); remaining.set(key, (remaining.get(key) ?? 0) + 1);
  }
  let matchedTriangles = 0, onlyRightTriangles = 0;
  for (let offset = 0; offset < rightGeometry.values.length; offset += 9) {
    const key = comparisonTriangleKey(rightGeometry.values, offset), count = remaining.get(key) ?? 0;
    if (count > 0) { matchedTriangles += 1; if (count === 1) remaining.delete(key); else remaining.set(key, count - 1); }
    else onlyRightTriangles += 1;
  }
  let onlyLeftTriangles = 0; for (const count of remaining.values()) onlyLeftTriangles += count;
  const left = summarizeComparisonSide(leftGeometry.values, leftGeometry.count), right = summarizeComparisonSide(rightGeometry.values, rightGeometry.count), union = matchedTriangles + onlyLeftTriangles + onlyRightTriangles;
  return {
    left, right, matchedTriangles, onlyLeftTriangles, onlyRightTriangles,
    exactTriangleMultisetMatch: onlyLeftTriangles === 0 && onlyRightTriangles === 0,
    triangleJaccardPercent: union ? matchedTriangles / union * 100 : 100,
    dimensionDelta: right.bounds.size.map((value, axis) => value - left.bounds.size[axis]) as [number, number, number],
    surfaceAreaDelta: right.surfaceArea - left.surfaceArea,
    signedVolumeDelta: right.signedVolumeEstimate - left.signedVolumeEstimate
  };
}

function rotateVertex(x: number, y: number, z: number, degrees: [number, number, number]): [number, number, number] {
  const [rx, ry, rz] = degrees.map((value) => value * Math.PI / 180);
  let nextY = y * Math.cos(rx) - z * Math.sin(rx), nextZ = y * Math.sin(rx) + z * Math.cos(rx);
  y = nextY; z = nextZ;
  let nextX = x * Math.cos(ry) + z * Math.sin(ry); nextZ = -x * Math.sin(ry) + z * Math.cos(ry);
  x = nextX; z = nextZ;
  nextX = x * Math.cos(rz) - y * Math.sin(rz); nextY = x * Math.sin(rz) + y * Math.cos(rz);
  return [nextX, nextY, z];
}

export function transformStl(buffer: ArrayBuffer, transform: StlTransform): { buffer: ArrayBuffer; inspection: StlInspection } {
  if (transform.scale.some((value) => !Number.isFinite(value) || value === 0)) throw new Error("Every scale axis must be a finite, non-zero number.");
  if (transform.rotateDegrees.some((value) => !Number.isFinite(value))) throw new Error("Every rotation must be finite.");
  const { values, count } = readStlTriangleValues(buffer);
  const transformed = new Array<number>(values.length);
  const minimum = [Infinity, Infinity, Infinity];
  for (let index = 0; index < values.length; index += 3) {
    const rotated = rotateVertex(values[index] * transform.scale[0], values[index + 1] * transform.scale[1], values[index + 2] * transform.scale[2], transform.rotateDegrees);
    transformed[index] = rotated[0]; transformed[index + 1] = rotated[1]; transformed[index + 2] = rotated[2];
    for (let axis = 0; axis < 3; axis += 1) minimum[axis] = Math.min(minimum[axis], rotated[axis]);
  }
  if (transform.placeMinimumAtOrigin) for (let index = 0; index < transformed.length; index += 3) for (let axis = 0; axis < 3; axis += 1) transformed[index + axis] -= minimum[axis];

  const output = new ArrayBuffer(84 + count * 50), view = new DataView(output);
  new Uint8Array(output, 0, 80).set(new TextEncoder().encode("PartHarbor transformed STL").slice(0, 80));
  view.setUint32(80, count, true);
  for (let triangle = 0; triangle < count; triangle += 1) {
    const source = triangle * 9, base = 84 + triangle * 50;
    const ax = transformed[source], ay = transformed[source + 1], az = transformed[source + 2];
    const bx = transformed[source + 3], by = transformed[source + 4], bz = transformed[source + 5];
    const cx = transformed[source + 6], cy = transformed[source + 7], cz = transformed[source + 8];
    const ab = [bx - ax, by - ay, bz - az], ac = [cx - ax, cy - ay, cz - az];
    const normal = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
    const length = Math.hypot(...normal) || 1;
    for (let axis = 0; axis < 3; axis += 1) view.setFloat32(base + axis * 4, normal[axis] / length, true);
    for (let coordinate = 0; coordinate < 9; coordinate += 1) view.setFloat32(base + 12 + coordinate * 4, transformed[source + coordinate], true);
    view.setUint16(base + 48, 0, true);
  }
  return { buffer: output, inspection: inspectStl(output) };
}

function vertexKey(values: number[], offset: number): string {
  const clean = (value: number) => Object.is(value, -0) ? 0 : value;
  return `${clean(values[offset])},${clean(values[offset + 1])},${clean(values[offset + 2])}`;
}

function triangleAreaSquared(values: number[], offset: number): number {
  const ax = values[offset], ay = values[offset + 1], az = values[offset + 2];
  const abx = values[offset + 3] - ax, aby = values[offset + 4] - ay, abz = values[offset + 5] - az;
  const acx = values[offset + 6] - ax, acy = values[offset + 7] - ay, acz = values[offset + 8] - az;
  const x = aby * acz - abz * acy, y = abz * acx - abx * acz, z = abx * acy - aby * acx;
  return x * x + y * y + z * z;
}

function meshHealth(values: number[]): MeshHealthReport {
  const vertices = new Set<string>(), triangles = new Set<string>(), edges = new Map<string, number>();
  let degenerateTriangles = 0, duplicateTriangles = 0;
  for (let offset = 0; offset < values.length; offset += 9) {
    const keys = [vertexKey(values, offset), vertexKey(values, offset + 3), vertexKey(values, offset + 6)];
    keys.forEach((key) => vertices.add(key));
    if (triangleAreaSquared(values, offset) === 0) { degenerateTriangles += 1; continue; }
    const triangle = [...keys].sort().join("|");
    if (triangles.has(triangle)) { duplicateTriangles += 1; continue; }
    triangles.add(triangle);
    for (const [a, b] of [[keys[0], keys[1]], [keys[1], keys[2]], [keys[2], keys[0]]]) {
      const edge = a < b ? `${a}|${b}` : `${b}|${a}`;
      edges.set(edge, (edges.get(edge) ?? 0) + 1);
    }
  }
  let boundaryEdges = 0, nonManifoldEdges = 0;
  for (const count of edges.values()) { if (count === 1) boundaryEdges += 1; else if (count > 2) nonManifoldEdges += 1; }
  return { triangles: values.length / 9, uniqueVertices: vertices.size, degenerateTriangles, duplicateTriangles, boundaryEdges, nonManifoldEdges, watertightCandidate: boundaryEdges === 0 && nonManifoldEdges === 0 && degenerateTriangles === 0 && duplicateTriangles === 0 };
}

export function inspectStlMeshHealth(buffer: ArrayBuffer): MeshHealthReport {
  return meshHealth(readStlTriangleValues(buffer).values);
}

export function writeBinaryStlValues(values: number[], header: string): ArrayBuffer {
  const count = values.length / 9, output = new ArrayBuffer(84 + count * 50), view = new DataView(output);
  new Uint8Array(output, 0, 80).set(new TextEncoder().encode(header).slice(0, 80));
  view.setUint32(80, count, true);
  for (let triangle = 0; triangle < count; triangle += 1) {
    const source = triangle * 9, base = 84 + triangle * 50;
    const ax = values[source], ay = values[source + 1], az = values[source + 2];
    const ab = [values[source + 3] - ax, values[source + 4] - ay, values[source + 5] - az];
    const ac = [values[source + 6] - ax, values[source + 7] - ay, values[source + 8] - az];
    const normal = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
    const length = Math.hypot(...normal) || 1;
    for (let axis = 0; axis < 3; axis += 1) view.setFloat32(base + axis * 4, normal[axis] / length, true);
    for (let coordinate = 0; coordinate < 9; coordinate += 1) view.setFloat32(base + 12 + coordinate * 4, values[source + coordinate], true);
    view.setUint16(base + 48, 0, true);
  }
  return output;
}

export function repairStlConservatively(buffer: ArrayBuffer): ConservativeRepairResult {
  const values = readStlTriangleValues(buffer).values, kept: number[] = [], seen = new Set<string>();
  let removedDegenerateTriangles = 0, removedDuplicateTriangles = 0;
  for (let offset = 0; offset < values.length; offset += 9) {
    if (triangleAreaSquared(values, offset) === 0) { removedDegenerateTriangles += 1; continue; }
    const triangle = [vertexKey(values, offset), vertexKey(values, offset + 3), vertexKey(values, offset + 6)].sort().join("|");
    if (seen.has(triangle)) { removedDuplicateTriangles += 1; continue; }
    seen.add(triangle); kept.push(...values.slice(offset, offset + 9));
  }
  if (kept.length === 0) throw new Error("Conservative repair would leave no triangles; no output was created.");
  const output = writeBinaryStlValues(kept, "PartHarbor conservative repair");
  const reopened = readStlTriangleValues(output).values;
  return { buffer: output, before: meshHealth(values), after: meshHealth(reopened), removedDegenerateTriangles, removedDuplicateTriangles };
}

function readObjGeometry(buffer: ArrayBuffer): { vertices: [number, number, number][]; triangleValues: number[]; faces: number; groups: number; objects: number; materialLibraries: number; ignoredStatements: string[] } {
  if (buffer.byteLength === 0 || buffer.byteLength > 25 * 1024 * 1024) throw new Error("OBJ must be between 1 byte and the provisional 25 MiB limit.");
  const text = new TextDecoder("utf-8", { fatal: false }).decode(buffer), vertices: [number, number, number][] = [], triangleValues: number[] = [];
  const ignored = new Set<string>();
  let faces = 0, groups = 0, objects = 0, materialLibraries = 0;
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim(); if (!line || line.startsWith("#")) continue;
    const [statement, ...parts] = line.split(/\s+/);
    if (statement === "v") {
      if (parts.length < 3) throw new Error("OBJ contains an incomplete vertex statement.");
      const vertex: [number, number, number] = [Number(parts[0]), Number(parts[1]), Number(parts[2])];
      if (vertex.some((value) => !Number.isFinite(value))) throw new Error("OBJ contains a non-finite vertex coordinate.");
      vertices.push(vertex); if (vertices.length > 2_000_000) throw new Error("OBJ exceeds the provisional 2,000,000-vertex limit.");
    } else if (statement === "f") {
      if (parts.length < 3) throw new Error("OBJ contains a face with fewer than three vertices.");
      const indices = parts.map((part) => {
        const raw = Number(part.split("/")[0]);
        if (!Number.isInteger(raw) || raw === 0) throw new Error("OBJ contains an invalid face index.");
        const index = raw > 0 ? raw - 1 : vertices.length + raw;
        if (index < 0 || index >= vertices.length) throw new Error("OBJ face references a vertex outside the parsed range.");
        return index;
      });
      faces += 1;
      for (let index = 1; index < indices.length - 1; index += 1) {
        triangleValues.push(...vertices[indices[0]], ...vertices[indices[index]], ...vertices[indices[index + 1]]);
        if (triangleValues.length / 9 > MAX_TRIANGLES) throw new Error(`OBJ exceeds the provisional ${MAX_TRIANGLES.toLocaleString()}-triangle limit.`);
      }
    } else if (statement === "g") groups += 1;
    else if (statement === "o") objects += 1;
    else if (statement === "mtllib") materialLibraries += 1;
    else if (!["vt", "vn", "usemtl", "s"].includes(statement)) ignored.add(statement);
  }
  if (vertices.length === 0 || triangleValues.length === 0) throw new Error("OBJ must contain vertices and at least one triangulatable face.");
  return { vertices, triangleValues, faces, groups, objects, materialLibraries, ignoredStatements: [...ignored].sort() };
}

export function inspectObj(buffer: ArrayBuffer, previewLimit = 8_000): ObjInspection {
  const parsed = readObjGeometry(buffer);
  const measured = inspectTriangles(parsed.triangleValues, "ascii", parsed.triangleValues.length / 9, previewLimit);
  return { vertices: parsed.vertices.length, faces: parsed.faces, triangles: measured.triangles, groups: parsed.groups, objects: parsed.objects, materialLibraries: parsed.materialLibraries, bounds: measured.bounds, surfaceArea: measured.surfaceArea, previewVertices: measured.previewVertices, previewTriangles: measured.previewTriangles, ignoredStatements: parsed.ignoredStatements };
}

function exactArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export function convertStlToObj(buffer: ArrayBuffer): MeshConversionResult {
  const source = readStlTriangleValues(buffer), vertexIndices = new Map<string, number>(), vertices: string[] = [], faces: string[] = [];
  for (let offset = 0; offset < source.values.length; offset += 9) {
    const face: number[] = [];
    for (let vertex = 0; vertex < 3; vertex += 1) {
      const coordinate = offset + vertex * 3, key = vertexKey(source.values, coordinate);
      let index = vertexIndices.get(key);
      if (!index) {
        index = vertexIndices.size + 1; vertexIndices.set(key, index);
        vertices.push(`v ${source.values[coordinate]} ${source.values[coordinate + 1]} ${source.values[coordinate + 2]}`);
      }
      face.push(index);
    }
    faces.push(`f ${face.join(" ")}`);
  }
  const text = `# PartHarbor STL to OBJ conversion\no partharbor_converted\n${vertices.join("\n")}\n${faces.join("\n")}\n`;
  const bytes = new TextEncoder().encode(text);
  if (bytes.byteLength > 100 * 1024 * 1024) throw new Error("Converted OBJ would exceed the provisional 100 MiB output limit.");
  const output = exactArrayBuffer(bytes), reopened = inspectObj(output, 1);
  if (reopened.triangles !== source.count) throw new Error("Generated OBJ did not reopen with the source triangle count.");
  return { buffer: output, sourceTriangles: source.count, outputTriangles: reopened.triangles, outputBytes: output.byteLength, outputFormat: "OBJ" };
}

export function convertObjToStl(buffer: ArrayBuffer): MeshConversionResult {
  const source = readObjGeometry(buffer), sourceTriangles = source.triangleValues.length / 9;
  const output = writeBinaryStlValues(source.triangleValues, "PartHarbor OBJ to STL conversion"), reopened = inspectStl(output, 1);
  if (reopened.triangles !== sourceTriangles) throw new Error("Generated STL did not reopen with the source triangle count.");
  return { buffer: output, sourceTriangles, outputTriangles: reopened.triangles, outputBytes: output.byteLength, outputFormat: "binary STL" };
}
